import React from "react";

import {createSlice, createAsyncThunk, current} from "@reduxjs/toolkit";
import { api, apiErrorsHandler, getErrorMessages } from "../../api/httpclient";
import { addNotification } from "./notificationsslice";
import { setAppDialog, hideAppDialog } from "./appdialogslice";
import { 
    WIDGET_NUMERIC_CARD, WIDGET_DATE_CARD, WIDGET_GAUGE_CARD, WIDGET_GRID, isChart,
     widgetsInfo, getWidgetSettingsComponent, getWidgetViewContextMenu, handleOnWidgetViewContextMenuClick,
     combineParameters, parameterHasValue, validateParameters, WIDGET_TICKER
} from "../../components/widgets/utils";
import { canEditReports } from "../../routing/authorizationhandlers";
import { toCamel, isNumeric, getJsonDateStringFromDateKey, getDateFromDateKey, dateOnlyString, dateTimeString, notificationDurationMiddle, isInteger  } from "../../lib/utils";
import { formatQuery} from 'react-querybuilder';
import { APP_TITLE } from "../../settings";

const buildReportObject = (pageState) =>
{
    let result = { 
        title: pageState.reportTitle, tags: pageState.reportTags, description: pageState.reportDescription,
        isLiveReport: pageState.isLiveReport, isFavorite: pageState.isFavorite, isSystem: pageState.isSystem, generateOnLoad: pageState.generateOnLoad,
        cardNumericUser: [], cardDateUser: [], cardGaugeUser: [], gridUser: [], genericChartUser: [], tickerUser: []
    };

    //handle if update
    result.id = pageState.reportId;

    for (let i in pageState.tiles)
    {
        let tile = pageState.tiles[i];
        
        let widget = {
            dataSourceId: tile.DataSourceId, 
            position: JSON.stringify(tile.defaultPosition)
        };

        const {
            id, widgetTypeId, type, subType, dataSourceValueType, mode, errorText, loaded, data, schema, loading, canceled,
            onWidgetSelect, onWidgetUnSelect, getContextMenu, onMenuItemClick, onDoubleClick,
            onColumnReorder, onColumnResize, onColumnSettingChange, onSort, onPageChange, onGroupChange, onFilter,
             ...otherProps
        } = tile.properties;

        widget = {...widget, ...otherProps};

        widget.id = tile.Id;

        //handle maximumRows
        if (isChart(tile.WidgetTypeId) || tile.WidgetTypeId == WIDGET_GRID || tile.WIDGET_TICKER)
        {            
            let maximumRows = widget.maximumRows;
            if (maximumRows == null || maximumRows == undefined || !isInteger(maximumRows.toString()) || parseInt(maximumRows.toString()) < 1)
            {
                let widgetInfo = widgetsInfo.find(e => e.id == tile.WidgetTypeId);
                maximumRows = widgetInfo.defaultSettings.maximumRows;
                widget.maximumRows = maximumRows;
            } 
        }

        if (tile.WidgetTypeId == WIDGET_NUMERIC_CARD)
            result.cardNumericUser.push(widget);
        else if (tile.WidgetTypeId == WIDGET_DATE_CARD)
            result.cardDateUser.push(widget);
        else if (tile.WidgetTypeId == WIDGET_GAUGE_CARD)
            result.cardGaugeUser.push(widget);
        else if (tile.WidgetTypeId == WIDGET_GRID)
            result.gridUser.push(widget);
        else if (isChart(tile.WidgetTypeId))
            result.genericChartUser.push({...widget, TypeId: widgetTypeId, sortDirectionId: otherProps.sortFieldDirectionId, legendPositionId: otherProps.legendPosition, topCount: otherProps.topCount == null || otherProps.topCount == undefined ? 1000 : (isNumeric(otherProps.topCount.toString()) ? parseInt(otherProps.topCount) : 0)});
        else if (tile.WidgetTypeId == WIDGET_TICKER)
            result.tickerUser.push(widget);
    }    

     //solve the issue of having a numeric field (ex. errorThreshold) that is string in API and bad request when converting
     for (let i in result.cardNumericUser)
     {
         if (result.cardNumericUser[i].errorThreshold != null && result.cardNumericUser[i].errorThreshold != undefined)
             result.cardNumericUser[i].errorThreshold = result.cardNumericUser[i].errorThreshold.toString();
     }
 
     for (let i in result.cardGaugeUser)
     {
         if (result.cardGaugeUser[i].errorThreshold != null && result.cardGaugeUser[i].errorThreshold != undefined)
             result.cardGaugeUser[i].errorThreshold = result.cardGaugeUser[i].errorThreshold.toString();
     }
 
     for (let i in result.gridUser)
     {
         if (result.gridUser[i].errorValue != null && result.gridUser[i].errorValue != undefined)
             result.gridUser[i].errorValue = result.gridUser[i].errorValue.toString();
     }

//    console.log("PUSHED: ", result)
    return result;
};

const initialState = {
    pageLoaded: false, pageDisposed: true,
    processing: false, processingText: "",
    reportId: 0, reportReferenceKey: "", reportTitle: "", reportDescription: "", reportTags: "", reportIsInViewMode: true,
    isLiveReport: false, isFavorite: false, isDashboard: false, isSystem: false, mainWidgetTypeId: 0, generateOnLoad: false, searchSchema: null, searchSchemaTemp: null,
    zoom: 1,
    canEdit: false,
    currentWidget: null,
    datasets:[],
    tiles:[],
    globalGenerate: false, globalRequestId: null,
    globalFilter: {isVisible: false, whereObject: "", whereStatement: "", whereParameters: "", parameters: []},
    filterFormDialogOpen: false,
    filterForm: {},
    filterFormValueKeysDialogOpen: false,
    filterFormValueKeys: {},
    settingsFormDialogOpen: false,
    schedulePageOpen:false,
    settingsForm:{}
};

const reportingViewPageSlice = createSlice({
    name: "reportingViewPage",
    initialState: initialState,
    reducers:{
        setInitialState: (state, action) =>  ({...initialState}),//no payload

        setProcessing: (state, action) => {
            //payload: {processing=true, processingText=false}, when processing is false, processingText is "" 
            const {processing=true, processingText=""} = action.payload;

            return ({...state, processing: processing, processingText: processing==false ? "" : processingText});
        },

        setPageValue: (state, action) => ({...state, [action.payload.id]: action.payload.value}),//payload: {id: , value:}

        reportPageLoaded: (state, action) => {
            //payload: {data: the loaded api returned dto, currentUser}
            const pageState = current(state);

            if (pageState.pageDisposed)
                return state;

            let payload = action.payload.data;
            let currentUser = action.payload.currentUser;
            let datasets = payload.Datasets;
            let dataSources = payload.DataSources;
            let widgets = payload.Widgets;
            let parameters = [];

            let tiles = widgets.map(w => {
                //get the widget dataset and data source
                let dataSource = dataSources.find(e => e.DataSourceId == w.DataSourceId);
                //let dataset = datasets.find(e => e.DataSourceId == dataSource.Id);
                let dataset = datasets.find(e => e.Id == dataSource.DatasetId);//corrected may 2023

                const widgetTypeId = w.WidgetTypeId;

                const properties = {
                    id: w.Id, widgetTypeId: widgetTypeId, dataSourceValueType: w.DataSourceValueTypeId,
                    mode: 2, errorText: "", loaded: false, loading: false, canceled: false, 
                    data: null,
                    schema: dataSource.Schema,
                    onWidgetSelect: (props, dispatch) => dispatch(selectWidget(w.Id)),
                    onWidgetUnSelect: (props, dispatch) => dispatch(unSelectWidget()),
                    getContextMenu: props => getWidgetViewContextMenu(widgetTypeId, false, false, false, ""),
                    onMenuItemClick: handleOnWidgetViewContextMenuClick,   
                    onDoubleClick: (id, props, dispatch) => dispatch(showSettingsForm(id)),
                    ...toCamel(w.Settings)
                };

                if (widgetTypeId == WIDGET_GRID)
                {
                    properties.currentPageIndex = 0;                    

                    properties.fields = properties.fields.map(f => {
                        let dataType = dataSource.Schema.find(e => e.Name == f.name).DataTypeId;
                        if (dataType == 1)
                            return {...f, filter: "boolean"};
                        else if (dataType == 2)
                            return {...f, filter: "numeric"};
                        else if (dataType == 3)
                            return {...f, filter: "text"};
                        else if (dataType == 4 || dataType == 5)
                            return {...f, filter: "date"};
                    });

                    //added on 18-04-1024
                    properties.tempFilter = {filters: [], logic: "and"};

                    properties.onTempFilter = (dispatch, widgetId, filter) => {
                        dispatch(selectWidget(widgetId));

                        dispatch(onGridTempFilter(filter));
                    };
                    //added on 18-04-1024


                     //events handlers
                     properties.onColumnReorder= (dispatch, widgetId, columns) => {
                        dispatch(selectWidget(widgetId));

                        dispatch(onGridColumnReorder({columns: columns}));
                    };

                    properties.onColumnResize= (dispatch, widgetId, columnId, oldWidth, newWidth, columns, end) => {
                        if (!end)
                            return;
                        
                        dispatch(selectWidget(widgetId));
                        
                        dispatch(onGridColumnResize({columnId: columnId, oldWidth: oldWidth, newWidth: newWidth, end: end}));
                    };
                    
                    properties.onColumnSettingChange= (dispatch, widgetId, fieldId, fieldName, prop, value) => {
                        dispatch(selectWidget(widgetId));

                        dispatch(onGridColumnSettingChange({fieldId: fieldId, fieldName: fieldName, prop: prop, value: value}));
                    };

                    properties.onSort= (dispatch, widgetId, sort) => {
                        dispatch(selectWidget(widgetId));

                        dispatch(onGridSort({sort: sort}));
                    };

                    properties.onPageChange = (dispatch, widgetId, page) => {
                        dispatch(selectWidget(widgetId));

                        dispatch(onGridPageChange({page: page}));
                    };

                    properties.onGroupChange = (dispatch, widgetId, group) => {
                        dispatch(selectWidget(widgetId));

                        dispatch(onGridGroupChange({group: group}));
                    };
                }
                // else if (isChart(widgetTypeId))
                // {
                //     properties.fields = dataSource.Schema.map(f => ({id: f.Id, title: f.Title, name: f.Name, dataTypeId: f.DataTypeId, position: f.Position}));
                // }   
                // else if (widgetTypeId == WIDGET_TICKER)
                // {
                //     properties.fields = w.Settings.Fields;
                // }                

                if (dataSource.DatasetIsParametrized)
                {                    
                    //create the widget parameters with default value = Value
                    parameters = datasets.find(e => e.Id == dataSource.DatasetId).Parameters.map(p => {
                        let defaultValue = p.DefaultValue;

                        if (p.DataTypeId == 1)
                        {
                            if (defaultValue)
                            {
                                if (defaultValue.trim().toLowerCase() == "true")
                                    return {...p, Value:  true};
                                else if (defaultValue.trim().toLowerCase() == "false")
                                    return {...p, Value:  false};
                                else
                                    return {...p, Value:  null};
                            }
                            else
                                return {...p, Value:  null};
                        }
                        else if (p.DataTypeId == 2)
                        {
                            if (p.IsLookup)
                            {
                                if (p.AllowMultiple)
                                {
                                    if (defaultValue && defaultValue.trim() != "")
                                    {
                                        let ids = defaultValue.split(",");
    
                                        if (ids && ids.length > 0)
                                        {
                                            let arr = [];
                                            for (let v in ids)
                                            {
                                                let lkp = p.LookupValues.find(e => e.Id == parseInt(ids[v]));
                                                if (lkp)
                                                   arr.push({...lkp});
                                            }
    
                                            return {...p, Value: arr};
                                        }  
                                        else
                                            return {...p, Value: []};
                                    }    
                                    else 
                                     return {...p, Value: []};
                                }
                                else    
                                    return {...p, Value: (defaultValue && isNumeric(defaultValue) ? parseInt(defaultValue) : 0)};
                            }
                            else    
                                return {...p, Value: isNumeric(defaultValue) ? defaultValue : ""};
                        }
                        else if (p.DataTypeId == 3)
                            return {...p, Value: (defaultValue === null || defaultValue === undefined) ? "" : defaultValue};   
                        else if (p.DataTypeId == 4)                    
                            return {...p, Value: defaultValue ? new Date(getJsonDateStringFromDateKey(defaultValue, false)) : null};       
                        else if  (p.DataTypeId == 5)                    
                            return {...p, Value: defaultValue ? new Date(getJsonDateStringFromDateKey(defaultValue, true)) : null};              
                    });
                }

                return {
                    Id: w.Id, WidgetTypeId: widgetTypeId, //TypeId: w.TypeId, SubTypeId:w.SubTypeId, 
                    DatasetId: w.DatasetId, DatasetCategory: dataset.Category, DatasetIsParametrized: dataset.IsParametrized, 
                    DataSourceId: w.DataSourceId, DataSourceTypeId: w.DataSourceTypeId, DataSourceValueTypeId: w.DataSourceValueTypeId,
                    defaultPosition: JSON.parse(w.Settings.DefaultPosition),
                    reorderable: properties.reorderable,
                    resizable: properties.resizable,
                    properties: properties,
                    item: getWidgetSettingsComponent(widgetTypeId, properties),                   
                    requestId: null, whereObject: "", whereStatement: "", whereParameters: "", combineWhere: true, combineWhereOperator: 1,                    
                    parameters: parameters, combineParameters: true
                };
            });//end of tiles creation


            let globalParameters = [];
            if (datasets.length == 1 && datasets[0].IsParametrized)
            {
                globalParameters = datasets[0].Parameters.map(p => {
                    let defaultValue = p.DefaultValue;

                    if (p.DataTypeId == 1)
                    {
                        if (defaultValue)
                        {
                            if (defaultValue.trim().toLowerCase() == "true")
                                return {...p, Value:  true};
                            else if (defaultValue.trim().toLowerCase() == "false")
                                return {...p, Value:  false};
                            else
                                return {...p, Value:  null};
                        }
                        else
                            return {...p, Value:  null};
                    }
                    else if (p.DataTypeId == 2)
                    {
                        if (p.IsLookup)
                        {
                            if (p.AllowMultiple)
                            {
                                if (defaultValue && defaultValue.trim() != "")
                                {
                                    let ids = defaultValue.split(",");

                                    if (ids && ids.length > 0)
                                    {
                                        let arr = [];
                                        for (let v in ids)
                                        {
                                            let lkp = p.LookupValues.find(e => e.Id == parseInt(ids[v]));
                                            if (lkp)
                                               arr.push({...lkp});
                                        }

                                        return {...p, Value: arr};
                                    }  
                                    else
                                        return {...p, Value: []};
                                }   
                                else 
                                    return {...p, Value: []};
                            }
                            else    
                                return {...p, Value:  (defaultValue && isNumeric(defaultValue) ? parseInt(defaultValue) : 0)};
                        }
                        else    
                            return {...p, Value: isNumeric(defaultValue) ? defaultValue : ""};
                    }
                    else if (p.DataTypeId == 3)
                        return {...p, Value: (defaultValue === null || defaultValue === undefined) ? "" : defaultValue};
                    else if (p.DataTypeId == 4)                    
                        return {...p, Value: defaultValue ? new Date(getJsonDateStringFromDateKey(defaultValue, false)) : null};       
                    else if  (p.DataTypeId == 5)                    
                        return {...p, Value: defaultValue ? new Date(getJsonDateStringFromDateKey(defaultValue, true)) : null};               
                });
            }

            document.title = APP_TITLE + " " + payload.Title;
 
            let finalGlobalFilter = { isVisible: datasets.length == 1, parameters: globalParameters, whereObject: "", whereStatement: "", whereParameters: ""};

            //handle generate on load
            let whereObject = "";
            let whereStatement = "";
            let whereParameters = "";
            if (payload.GenerateOnLoad == true && datasets.length == 1 && payload.SearchSchema)
            {
                whereObject = JSON.parse(payload.SearchSchema.WhereObject);
                       
                //if no search => whereObject might be "" or an object with props; in the latter case, whereParamateres will be [] and i'll rely on it to know 
                whereParameters = whereObject ? formatQuery(whereObject, 'parameterized').params : "";

                // let _rules = payload.SearchSchema.Rules;

                // let _temp = [];
                // for (let i in _rules)
                // {
                //     let _rule = _rules[i];
                //     let _value = _rule.Value;

                //     let _whereRule = whereObject.rules.find(e => e.id == _rule.RuleId);
                //     console.log(_whereRule);

                //     if (Array.isArray(_value))
                //     {
                //         _temp.push(_value[0]);
                //         _temp.push(_value[1]);

                //         if (_whereRule)
                //             _whereRule.value = `_${_value[0]},${_value[1]}`;
                //     }
                //     else 
                //        {
                //         _temp.push(_value);
                //         _whereRule.value = `_${_value}`;
                //        }

                // }

                // whereParameters = _temp;
                // console.log(">>>>", payload.SearchSchema, whereParameters)
                
                
                //whereParameters is null or [] if no search or a filled array if there's a search
                if((whereParameters != null && whereParameters.length > 0) || (whereObject.rules && whereObject.rules.length > 0))
                    whereStatement = formatQuery(whereObject, 'parameterized').sql; 
                    
                
                //CODE MUST BE CHANGED HERE WHEN INTRODUCING a parametrized dataset
                //parameters is the array including the Value entry for each entry
                // let parameters = formState.parameters.map(e => {
                //     return {...e, Value: formState.formValues[e.Name]};
                // });

                finalGlobalFilter = { isVisible: true, parameters: globalParameters, whereObject: whereObject, whereStatement: whereStatement, whereParameters: whereParameters };
            }
            //end of handle generate on load

            return {...state, 
                pageLoaded: true,
                processing: false, processingText: "",
                reportId: payload.Id, reportTitle: payload.Title, reportTags: payload.Tags, reportDescription: payload.Description,
                isLiveReport: payload.IsLiveReport, isFavorite: payload.IsFavorite, isDashboard: payload.IsDashboard, isSystem: payload.IsSystem, mainWidgetTypeId: payload.MainWidgetTypeId, generateOnLoad: payload.GenerateOnLoad,
                reportReferenceKey: payload.ReferenceKey, searchSchema: (payload.SearchSchema ? ({...payload.SearchSchema, WhereObject: JSON.parse(payload.SearchSchema.WhereObject)}) : null),
                datasets: datasets,
                dataSources: dataSources,
                currentWidget: null,
                tiles: tiles,
                globalFilter: finalGlobalFilter,
                canEdit: canEditReports(currentUser)
            };
        },

        setSettingsFormOuterValue: (state, action) => {
            //set a field inside settingsForm but NOT inside its formValues
            //payload: {id: fieldId, value: fieldValue}
            let id = action.payload.id;
            let value = action.payload.value;

            return ({
                ...state, 
                settingsForm: {...state.settingsForm, [id]: value}
            });
        },

        setSettingsFormValue: (state, action) => {
            //payload: {id: fieldId, value: fieldValue}
            let id = action.payload.id;
            let value = action.payload.value;

            return ({
                ...state, 
                settingsForm: {...state.settingsForm, formValues: {...state.settingsForm.formValues, [id]: value}}
            });
        },

        setSettingsFormSettingValue: (state, action) => {
            //payload: {id: fieldId, value: fieldValue}
            let id = action.payload.id;
            let value = action.payload.value;

            return ({
                ...state, 
                settingsForm: {...state.settingsForm, formValues: {...state.settingsForm.formValues, settings: {...state.settingsForm.formValues.settings, [id]: value}}}
            });
        },      

        /*settings form*/
        showSettingsForm: (state, action) => {
            //payload: widgetId
            const pageState = current(state);
            const formState = pageState.settingsForm;

            if (pageState.settingsFormDialogOpen)
                return state;

            const widgetId = pageState.currentWidget ? pageState.currentWidget.Id : action.payload;
            if (!widgetId)
                return state;

            const currentWidget = pageState.tiles.find(e => e.Id == widgetId);            
            const widgetTypeId = currentWidget.WidgetTypeId;
            const dataSource = pageState.dataSources.find(e => e.DataSourceId == currentWidget.DataSourceId);

            //if chart widget => add to fields 0 None
            let _properties = {...currentWidget.properties};
            let _fields = _properties.fields ? [..._properties.fields] : [];

            if (isChart(widgetTypeId))
                _fields.splice(0, 0, {id: 0, title: "None", name: "None"});
            else if (widgetTypeId == WIDGET_GRID || widgetTypeId == WIDGET_TICKER)
                _fields = _fields.sort((a,b) => (a.title <= b.title) ? -1 : 1);

                
            return {
                ...state, 
                currentWidget: currentWidget,
                settingsFormDialogOpen: true,
                settingsForm:{
                    mode: 2, widgetId: currentWidget.Id, widgetTypeId: currentWidget.WidgetTypeId, //type: currentWidget.TypeId, subType: currentWidget.SubTypeId, 
                    dataSources: [{Id: dataSource.DataSourceId, Title: dataSource.DataSourceTitle, IsImplicit: dataSource.DataSourceIsImplicit}],
                    dataSourceTypeId: dataSource.DataSourceTypeId, dataSourceValueTypeId: dataSource.DataSourceValueTypeId,
                    pageIndex: 1,
                    formKey:(new Date()).toString(),
                    formValues: {
                        id: currentWidget.Id, widgetTypeId: currentWidget.WidgetTypeId, //type: currentWidget.TypeId, subType: currentWidget.WidgetSubType, 
                        datasetId: dataSource.DatasetId, dataSourceId: dataSource.DataSourceId, 
                        settings:{...currentWidget.properties, fields: _fields}
                    },
                    formInitialValues: {
                        id: currentWidget.Id, widgetTypeId: currentWidget.WidgetTypeId, //type: currentWidget.TypeId, subType: currentWidget.WidgetSubType, 
                        datasetId: dataSource.DatasetId, dataSourceId: dataSource.DataSourceId, 
                        settings:{...currentWidget.properties, fields: _fields}
                    }
                }
            };            
        },

        settingsFormSave: (state, action) => {
            const pageState = current(state);
            const formState = pageState.settingsForm;
            
            const dataSourceId = formState.formValues.dataSourceId;
            const dataSourceTypeId = formState.dataSourceTypeId;
            const dataSource = pageState.dataSources.find(e => e.DataSourceId == dataSourceId);
            const widgetInfoEntry = widgetsInfo.find(e => e.id == formState.widgetTypeId);

            let tile = pageState.tiles.find (e => e.Id == formState.widgetId);

            const {fields, ...nonFieldsSettings} = formState.formValues.settings;

            //fields require special care
            let properties = {
                ...tile.properties,
                id: formState.widgetId, widgetTypeId: formState.widgetTypeId, //type: formState.type, subType: formState.subType, 
                dataSourceValueType: formState.dataSourceValueTypeId,
                ...nonFieldsSettings,                
            };        
            
            if (formState.widgetTypeId == WIDGET_GRID && fields.filter(e => e.isVisible == true).length > 0)
            {
                properties.fields =  tile.properties.fields.map((f) => {
                    let _f = fields.find(e => e.id == f.id);
                    if (_f.isVisible)
                        return {...f, isVisible: true};
                    else
                        return {...f, isVisible: false};
                });
            }

            if (formState.widgetTypeId == WIDGET_TICKER)
                properties.fields =  [...fields];


            if (formState.widgetTypeId == WIDGET_GRID || isChart(formState.widgetTypeId) || formState.widgetTypeId == WIDGET_TICKER)
            {
                let maximumRows = properties.maximumRows;
                if (maximumRows == null || maximumRows == undefined || !isInteger(maximumRows.toString()) || parseInt(maximumRows.toString()) < 1)
                    properties.maximumRows = widgetInfoEntry.defaultSettings.maximumRows;
            }

            return {...state,
                settingsFormDialogOpen: false,
                tiles: state.tiles.map(tile => {
                    if (tile.Id == formState.widgetId)
                        return {...tile, 
                            properties: {...properties},
                            item: getWidgetSettingsComponent(formState.widgetTypeId, properties),
                            reorderable: properties.reorderable, resizable: properties.resizable
                        };
                    else
                        return tile;
                })
            };            
        },

        closeSettingsForm: (state, action) => {
            return {...state, settingsFormDialogOpen: false}
        },       
        /*settings form*/

        redirectToSchedulePage: (state, action) => { 
            //invoked when you double click a widget or right click it and choose select
            const pageState = current(state); 

            if (pageState.schedulePageOpen)
                return state;

            const widgetId = pageState.currentWidget ? pageState.currentWidget.Id : action.payload;
            if (!widgetId)
                return state;

            const currentWidget = pageState.tiles.find(e => e.Id == widgetId);
        
            return {
                ...state, 
                currentWidget: currentWidget,
                schedulePageOpen: true,           
            };
    
        },
        
        // closeSchedulePopup: (state, action) => {
        //     debugger;
        //     return {...state, schedulePageOpen: false}
        // },


        /*filter form*/
        showFilterForm: (state, action) => {
            //payload: widgetId
            const pageState = current(state);
            const widgetId = action.payload;
            const isGlobal = (widgetId == 0 || widgetId == null);
            let tile = null;

            //double check to verify that is global => we have only one dataset
            //if we attempt to show the global filter form and 2+ datasets exist, the call is ignored; of course, the ui
            //shall not show the button but even if it did, the filter form won't open because state itself is returned
            if (isGlobal && pageState.datasets.length > 1)
                return state;

            //if global is visible => we have one dataset and it'll be selected; else, dataset is the dataset of the widget
            let dataset = {};
            if (isGlobal==true)
                dataset = pageState.datasets[0];
            else
            {
                tile = pageState.tiles.find(e => e.Id == widgetId);
                dataset = pageState.datasets.find(e => e.Id == tile.DatasetId);
            }

            let whereFields = dataset.Fields.filter(e => e.IsAggregate == false).map(e => {
                let inputType="bool";

                if (e.DataTypeId==2)
                    inputType = "number";
                else if (e.DataTypeId == 3)
                    inputType = "string";
                else if (e.DataTypeId == 4 || e.DataTypeId == 5)
                    inputType = "date";

                return {...e, name: e.Name, label: e.Title, inputType: inputType, Values: e.LookupValues || []};
            });

            let parameters = dataset.IsParametrized ? (isGlobal ? pageState.globalFilter.parameters : tile.parameters) : [];
            let parametersObject = {};
            if (parameters.length > 0)
            {
                for (let i in parameters)
                    parametersObject[parameters[i].Name] = parameters[i].Value;
            }

            return ({...state,  
                filterFormDialogOpen: true,
                filterForm: {
                    isGlobal: isGlobal, pageIndex: dataset.IsParametrized ? 0 : 1,
                    widgetId: widgetId, widgetTypeId: widgetId ? tile.properties.widgetTypeId : 0,
                    datasetId: dataset.Id, datasetCategory: dataset.Category, datasetIsParametrized: dataset.IsParametrized,
                    parameters: parameters,
                    combineParameters: isGlobal ? false : tile.combineParameters,
                    formKey: (new Date().toString()), formInitialValues: parametersObject, formValues: parametersObject,                    
                    whereObject: isGlobal ? state.globalFilter.whereObject : tile.whereObject,
                    whereStatement: isGlobal ? state.globalFilter.whereStatement : tile.whereStatement, 
                    whereParameters: isGlobal ? state.globalFilter.whereParameters : tile.whereParameters, 
                    combineWhere: isGlobal ? false : tile.combineWhere,
                    combineWhereOperator: isGlobal ? false : tile.combineWhereOperator,
                    whereFields: whereFields
                },
               
            });
        },

        setFilterFormValue: (state, action) => {
            //payload: {id: fieldId, value: fieldValue}
            let id = action.payload.id;
            let value = action.payload.value;

            return ({
                ...state, 
                filterForm: {...state.filterForm, formValues: {...state.filterForm.formValues, [id]: value}}
            });
        },
        
        setFilterFormOuterValue: (state, action) => {
            //payload: {id: fieldId, value: fieldValue} 
            let id = action.payload.id;
            let value = action.payload.value;

            return ({
                ...state, 
                filterForm: {...state.filterForm, [id]: value}
            });
        },

        setFilterFormParameter: (state, action) => {
            let name = action.payload.name;
            let value = action.payload.value;

            return ({...state, 
                filterForm: {...state.filterForm, 
                    formValues: {
                        ...state.filterForm.formValues, 
                        [name]: value
                    }
                }}
            );
        },

        filterFormPrevious : (state, action) => ({...state, filterForm: {...state.filterForm, pageIndex: 0}}),

        filterFormNext : (state, action) => ({...state, filterForm: {...state.filterForm, pageIndex: 1}}),

        filterFormSave: (state, action) => {
            const pageState = current(state);
            const formState = pageState.filterForm;

            let whereObject = formState.whereObject;
                       
            //if no search => whereObject might be "" or an object with props; in the latter case, whereParamateres will be [] and i'll rely on it to know 
            //if there's a search or not; a search exists if whereParameters && whereParamters.length > 0
            let whereParameters = whereObject ? formatQuery(whereObject, 'parameterized').params : null;
            let whereStatement = "";

            //whereParameters is null or [] if no search or a filled array if there's a search
            if((whereParameters != null && whereParameters.length > 0) || (whereObject.rules && whereObject.rules.length > 0))
                whereStatement = formatQuery(whereObject, 'parameterized').sql; 
                
            //parameters is the array including the Value entry for each entry
            let parameters = formState.parameters.map(e => {
                return {...e, Value: formState.formValues[e.Name]};
            });

            if (formState.isGlobal)
                return {...state, 
                    filterFormDialogOpen: false,
                    globalFilter: {...state.globalFilter, 
                        parameters: parameters,
                        whereObject: whereObject, 
                        whereParameters: (whereParameters == null) ? [] : whereParameters,// whereObject ? whereParameters : state.globalFilter.whereParameters,
                        whereStatement: whereStatement// whereObject ? whereStatement : state.globalFilter.whereStatement, 
                    },
                };
            else
                return {...state, 
                    filterFormDialogOpen: false,
                    tiles: state.tiles.map(e => {
                        if (e.Id == formState.widgetId)
                            return {...e, 
                                parameters: parameters, 
                                combineParameters: formState.combineParameters,
                                whereObject: whereObject, 
                                whereParameters: (whereParameters == null) ? [] : whereParameters,//whereObject ? whereStatement : e.whereStatement, 
                                whereStatement: whereStatement,// whereObject ? whereParameters : e.whereParameters,
                                combineWhere: formState.combineWhere,
                                combineWhereOperator: formState.combineWhereOperator
                            };
                        else
                            return e;
                    })
                }
        },

        closeFilterForm: (state, action) => {
            //i am not clearing the form values relying on that showFilterForm() will initialize them all from globalFilter or tile
            return {...state, filterFormDialogOpen: false}
        },

        clearFilterForm: (state, action) => {
            return {...state, filterForm: {...state.filterForm, whereObject: "", whereStatement: "", whereParameters: ""}};
        },

        showFilterFormValueKeys: (state, action) => {
            //payload: widgetId
            const pageState = current(state);         
            const rules = pageState.searchSchemaTemp.rules;

            let parameters = rules.filter(e => e.parameterId && e.parameterId > 0 && (e.dataTypeId == 4 || e.dataTypeId == 5));
            let fields = rules.filter(e => (!e.parameterId || e.parameterId <= 0) && (e.dataTypeId == 4 || e.dataTypeId == 5));

            let formFields = {};

            ////////////////////////////
            //handle parameters as below
            ////////////////////////////


            for (let k in fields)
            {
                let entry = fields[k];

                let componentId = entry.fieldName + entry.ruleId.replace("-", "").replace(".", "");
                formFields[componentId] = "0";//start with default date key
            }

            return ({...state,  
                filterFormDialogOpen: false,
                filterFormValueKeysDialogOpen: true,
                filterFormValueKeys: {
                    formKey: (new Date().toString()), 
                    formInitialValues: {...formFields}, 
                    formValues: {...formFields}
                    }
                });
        },

        searchSchemaSaved: (state, action) => {
            //payload: {data: the loaded api returned dto}
            const pageState = current(state);

            if (pageState.pageDisposed)
                return state;

            let data = action.payload.data;

            return {
                ...state, 
                filterFormValueKeysDialogOpen: false, //was forced to close all because if user saves then clicks load the querybuilder does not reflect the new valued
                filterFormDialogOpen: false,//because queryeditor refreshes if operator or field changes not values
                searchSchema: data.Id == 0 ? null :
                 {
                    Id: data.Id, Title: data.Title, DatasetId: data.DatasetId, LoadByDefault: data.LoadByDefault,
                    WhereObject: JSON.parse(data.WhereObject),
                    Rules: data.Rules
                }};            
        },

        setFilterFormValueKeysValue: (state, action) => {
            //payload: {ruleId, fieldName, parameterId, dataTypeId, value}
            const pageState = current(state);
            const payload = action.payload;
            const ruleId = payload.ruleId;
            const dataTypeId = payload.dataTypeId;
            const operatorName = payload.operatorName;
            const componentId = payload.componentId;
            const valueKey = payload.valueKey;//the new set value

            let obj = {...pageState.filterFormValueKeys.formValues};
            
            return {
                ...state, 
                searchSchemaTemp: {
                    ...state.searchSchemaTemp, 
                    rules: state.searchSchemaTemp.rules.map(e => {
                        if (!e.rules)//single level rule
                        {
                            if (e.ruleId == ruleId)
                                return {...e, valueKey: valueKey};                        
                            else 
                                return e;
                        }
                        else//has children rules => ignore replaceing date key
                            return e;
                    })
                },
                filterFormValueKeys: {
                    ...state.filterFormValueKeys, 
                    formValues: {
                        ...state.filterFormValueKeys.formValues, 
                        [componentId]: valueKey
                    }}
                };
        },

        closeFilterFormValueKeys: (state, action) => {
            //i am not clearing the form values relying on that showFilterForm() will initialize them all from globalFilter or tile
            return {...state,                 
                filterFormValueKeysDialogOpen: false,
                filterFormValueKeys:{
                    formKey:"", forInitialValues:{}, formValues:{}
                },
                filterFormDialogOpen: true,//reopen the filter form
            }
        },

        loadSchemaSearch: (state, acton) => {
            const pageState = current(state);

            let basicWhereObject = pageState.searchSchema.WhereObject;

            let basicRules = pageState.searchSchema.Rules;

            let whereObject = {...basicWhereObject, rules: []};

            for (let r in basicWhereObject.rules)
            {               
                let nativeRule = {...basicWhereObject.rules[r]};
                let _rule = basicRules.find(e => e.RuleId == nativeRule.id);
                let toAddRule = {...nativeRule};
            
                if (_rule.DataTypeId == 4 || _rule.DataTypeId == 5)//if(_rule.ValueKey)
                {
                    let v = "";

                    if (_rule.ValueKey && _rule.ValueKey != "0")
                    {
                        ////v = getJsonDateStringFromDateKey(_rule.ValueKey);
                        v = getDateFromDateKey(_rule.ValueKey);
                    }
                    else
                    {
                        if (_rule.Value.toString().indexOf(",") > 0)
                            v = _rule.Value.split(",");
                        else
                            v = _rule.Value;
                    }

                    // if (_rule.Value.toString().indexOf(",") > 0)
                    //     v = _rule.Value.split(",");
                    // else   
                    //     v = getJsonDateStringFromDateKey(_rule.ValueKey);

                    if (_rule.OperatorName == "between")
                    {
                        if (_rule.DataTypeId == 4)                        
                            toAddRule.value = [dateOnlyString(new Date(v[0])), dateOnlyString(new Date(v[1]))];
                        else
                            toAddRule.value = [dateTimeString(new Date(v[0])), dateTimeString(new Date(v[1]))];
                    }
                    else
                    {
                        if (_rule.DataTypeId == 4)                        
                            toAddRule.value = dateOnlyString(new Date(v));
                        else
                            toAddRule.value = dateTimeString(new Date(v));
                    }
                }

                whereObject.rules.push(toAddRule);
            }    
            
            return {
                ...state,
                filterForm: {...state.filterForm, whereObject: whereObject}
            };
        },
        /*filter form*/


        selectWidget: (state, action) => {           
            //payload: id
            const pageState = current(state);
            const id = action.payload;
            const widget = pageState.tiles.find(e => e.Id == id);

            return {...state, currentWidget: widget};
        },

        unSelectWidget: (state, action) => {
            const pageState = current(state);
            const widget = pageState.currentWidget;

            if(!widget)
                return state;

            return {...state, currentWidget: null};
        },

        resetTileWidget: (state, action) => {
            //redraws the widget - for example after mouse up of resize tile
            const pageState = current(state);
            let tileId = action.payload;

            return {
                ...state,
                tiles: pageState.tiles.map(
                    t => {
                        if (t.Id != tileId)
                            return {...t};
                        else                        
                            return {...t, item: getWidgetSettingsComponent(t.WidgetTypeId, {...t.properties, redrawProp: (new Date().toString())})}                        
                    }
                )
            }
        },

        zoomIn: (state, action) => {
            const pageState = current(state);
            let zoom = pageState.zoom;
            let result = 0;

            if (zoom == 0.25)
                result = 0.5;
            else if (zoom == 0.5)
                result = 0.75;
            else if (zoom == 0.75)
                result = 1;
            else if (zoom == 1)
                result = 1.25;
            else if (zoom == 1.25)
                result = 1.5;
            else if (zoom == 1.5)
                result = 1.75;
            else //if (zoom == 1.75)
                result = 2;

            return {...state, zoom: result};
        },

        zoomOut: (state, action) => {
            const pageState = current(state);
            let zoom = pageState.zoom;
            let result = 0;

            if (zoom == 2)
                result = 1.75;
            else if (zoom == 1.75)
                result = 1.5;
            else if (zoom == 1.5)
                result = 1.25;
            else if (zoom == 1.25)
                result = 1;
            else if (zoom == 1)
                result = 0.75;
            else if (zoom == 0.75)
                result = 0.5;
            else //if (zoom == 1.75)
                result = 0.25;

            return {...state, zoom: result};
        },


        //NOOO
        setTiles: (state, action) => {
            const pageState = current(state);

            let widgets = action.payload;
            
            return {...state,
                tiles: pageState.tiles.map(t => {
                    let widget = widgets.find(e => e.widgetId == t.Id);                    

                    let _properties = {
                        ...t.properties, 
                        loaded: widget.loaded, 
                        loading: widget.loading, 
                        canceled: widget.canceled, 
                        errorText: widget.errorText,
                        getContextMenu: props => getWidgetViewContextMenu(widget.widgetTypeId, widget.loaded, widget.loading, widget.canceled, widget.errorText),
                    };

                    return {
                        ...t, 
                        requestId: widget.requestId, 
                        properties: _properties,
                        item: getWidgetSettingsComponent(widget.widgetTypeId, _properties) 
                    }
                })
            };
        },

        reportViewGeneratedSucceeded: (state, action) => {
            //payload: { userId, widgetsIds:[], requestId, data, dataSourceTypeId}
            const pageState = current(state);
            const payload = action.payload;

            const requestId = payload.requestId;
            const widgetsIds = payload.widgetsIds;
            const dataSourceTypeId = payload.dataSourceTypeId;
            const data = payload.data;
           
            let _data = null;

            let tiles = pageState.tiles.filter(e => widgetsIds.indexOf(e.Id) >= 0 && e.requestId == requestId);
            if (!tiles || tiles.length == 0)
            {
                //for an extra measure, let's check if the request id is the global one and no widgets at all has this request id => something strange prevented
                //global request id from being null so let's annulate it
                if (pageState.globalRequestId && pageState.globalRequestId == requestId && !pageState.tiles.filter(e => e.requestId == requestId))
                   return {...state, globalRequestId: null, globalGenerate: false};
                else
                    return state;
            }


            if (dataSourceTypeId == 1)//value
            {
                if (data == "null")
                    _data = null;
                else
                    _data = data;
            }
            else
            {
                //table
                if (data == "null" || data == null || data == undefined)
                    _data = [];
                else
                    _data = JSON.parse(data);
            }

            //recompute globalGenerate            
            let globalGenerate = pageState.globalGenerate;
            let globalRequestId = pageState.globalRequestId;

            if (globalGenerate && globalRequestId == requestId)
            {
                //this is part of global request => might be the last request => check is other widget still pending in same request
                let isGlobalPending = pageState.tiles.find(e => widgetsIds.indexOf(e.Id) < 0 && e.RequestId == globalRequestId);
                if (!isGlobalPending)
                    globalGenerate = false;
            }

            let _tiles = pageState.tiles.map(e =>{
                if (e.requestId == requestId && widgetsIds.indexOf(e.Id) >= 0)
                {                                    
                    let _properties = {
                        ...e.properties, 
                        loaded: true, loading: false, errorText: "", canceled: false, 
                        getContextMenu: props => getWidgetViewContextMenu(e.WidgetTypeId, true, false, "", false),
                        data: dataSourceTypeId ==  1 ? _data :  _data.slice(0, e.properties.maximumRows)//(dataSourceTypeId == 1 ? ((data == "null" || data == null) ? null : data) : ((data == "null" || data == null) ? data : ))
                    };

                    return {...e, 
                            requestId: null, 
                            properties: _properties,
                            item: getWidgetSettingsComponent(e.WidgetTypeId, _properties) 
                        };
                }                    
                else
                    return e;
            });

            return {
                ...state, 
                globalGenerate: globalGenerate,
                tiles: _tiles
            };
        },

        reportViewGeneratedFailed: (state, action) => {
            //payload: { userId, widgetsIds:[], requestId, errorText}
            const pageState = current(state);
            const payload = action.payload;

            const requestId = payload.requestId;
            const widgetsIds = payload.widgetsIds;
            const errorText = payload.errorText;

            let tile = pageState.tiles.find(e => widgetsIds.indexOf(e.Id) >= 0 && e.requestId == requestId);
            if (!tile)
            {       
                //for an extra measure, let's check if the request id is the global one and no widgets at all has this request id => something strange prevented
                //global request id from being null so let's annulate it
                if (pageState.globalRequestId && pageState.globalRequestId == requestId && !pageState.tiles.filter(e => e.requestId == requestId))
                   return {...state, globalRequestId: null, globalGenerate: false};
                else
                    return state;
            }      
         
            //recompute globalGenerate            
            let globalGenerate = pageState.globalGenerate;
            let globalRequestId = pageState.globalRequestId;
            if (globalGenerate && globalRequestId == requestId)
            {
                //this is part of global request => might be the last request => check is other widget still pending in same request
                let isGlobalPending = pageState.tiles.find(e => widgetsIds.indexOf(e.Id) < 0 && e.RequestId == globalRequestId);
                if (!isGlobalPending)
                    globalGenerate = false;
            }

            let _tiles = pageState.tiles.map(e =>{
                if (e.requestId == requestId && widgetsIds.indexOf(e.Id) >= 0)
                {
                    let _properties = {
                        ...e.properties, 
                        loaded: false, loading: false, errorText: errorText, canceled: false,
                        getContextMenu: props => getWidgetViewContextMenu(e.WidgetTypeId, false, false, errorText, false),
                    };

                    return {...e, 
                            requestId: null, 
                            properties: _properties,
                            item: getWidgetSettingsComponent(e.WidgetTypeId, _properties) 
                        };
                }                    
                else
                    return e;
            });

            return {
                ...state, 
                globalGenerate: globalGenerate,
                tiles: _tiles
            };

            // return {
            //     ...state, 
            //     globalGenerate: globalGenerate,
            //     tiles: state.tiles.map(e =>{
            //         if (e.Id == tile.Id)
            //         {
            //             let _properties = {
            //                 ...tile.properties, 
            //                 loaded: false, loading: false, errorText: errorText, canceled: false,
            //                 getContextMenu: props => getWidgetViewContextMenu(tile.WidgetTypeId, false, false, errorText, false),
            //             };

            //             return {...e, 
            //                 requestId: null, 
            //                 properties: _properties,
            //                 item: getWidgetSettingsComponent(tile.WidgetTypeId, _properties) 
            //             };
            //         }
            //         else
            //             return e;
            //     })
            // }
        },

        globalCancelReport: (state, action) => {
            const pageState = current(state);
            const globalRequestId = pageState.globalRequestId;
            const tiles = pageState.tiles;

            return {...state,
                globalGenerate: false, globalRequestId: null,
                tiles: state.tiles.map(e => {
                    if (e.requestId == globalRequestId)
                    {
                        let tile = tiles.find(t => t.Id == e.Id);

                        let _properties = {
                            ...tile.properties, 
                            loaded: false, loading: false, errorText: "", canceled: true,
                            getContextMenu: props => getWidgetViewContextMenu(tile.WidgetTypeId, false, false, "", true),
                        };

                        return {...e, 
                            requestId: null, 
                            properties: _properties,
                            item: getWidgetSettingsComponent(e.WidgetTypeId, _properties) 
                        }
                    }
                    else
                        return state;
                })
            };
        },
        
        generateWidget: (state, action) => {
            //changes widget state either to error mode (if params exist and validation fails) or to loading mode
            const pageState = current(state);

            let widget = action.payload;
            
            let tile = pageState.tiles.find(e => e.Id == widget.widgetId);

            if (widget.errorText)
            {
                let properties = {...tile.properties, 
                    loaded: false, 
                    loading: false, 
                    errorText: widget.errorText, 
                    canceled: false,
                    getContextMenu: props => getWidgetViewContextMenu(widget.widgetTypeId, false, false, false, widget.errorText),
                };

                return {...state,
                    tiles: state.tiles.map(e => {
                        if (e.Id == widget.widgetId)
                            return {...e, 
                                requestId: widget.requestId, 
                                properties: properties,
                                item: getWidgetSettingsComponent(widget.widgetTypeId, properties) 
                            };
                        else
                            return e;
                    })
                };
            }
            else
            {
                let properties = {...tile.properties, 
                    loaded: false, 
                    loading: true, 
                    errorText: "", 
                    canceled: false,
                    getContextMenu: props => getWidgetViewContextMenu(tile.WidgetTypeId, false, true, false, ""),
                };

                return {...state,
                    tiles: state.tiles.map(e => {
                        if (e.Id == widget.widgetId)
                            return {...e, 
                                requestId: widget.requestId, 
                                properties: properties,
                                item: getWidgetSettingsComponent(widget.widgetTypeId, properties) 
                            };
                        else
                            return e;
                    })
                };
            }
        },

        gridExportToExcelOn: (state, action) => {
            //payload: widgetId
            const pageState = current(state);

            let widgetId = action.payload;

            let tile = pageState.tiles.find(e => e.Id == widgetId);

            return {...state,
                tiles: state.tiles.map(e => {
                    if (e.Id == widgetId)
                    {
                        let _properties = {...tile.properties, exportToExcel: true};

                        return {...e, 
                            properties: _properties,
                            item: getWidgetSettingsComponent(tile.WidgetTypeId, _properties) 
                        };
                    }
                    else
                        return e;
                })
            };
        },

        gridExportToExcelOff: (state, action) => {
            //payload: widgetId
            const pageState = current(state);

            let widgetId = action.payload;

            let tile = pageState.tiles.find(e => e.Id == widgetId);

            return {...state,
                tiles: state.tiles.map(e => {
                    if (e.Id == widgetId)
                    {
                        let _properties = {...tile.properties, exportToExcel: false};

                        return {...e, 
                            properties: _properties,
                            item: getWidgetSettingsComponent(tile.WidgetTypeId, _properties) 
                        };
                    }
                    else
                        return e;
                })
            };
        },

        cancelWidget: (state, action) => {
            //payload: widgetId
            const pageState = current(state);

            let widgetId = action.payload;
            let tile = pageState.tiles.find(e => e.Id == widgetId);
            if (!tile.requestId)
                return state;

            let properties = {
                ...tile.properties,
                loaded: false,
                loading: false,
                errorText: "",
                canceled: true,
                getContextMenu: props => getWidgetViewContextMenu(tile.WidgetTypeId, false, false, true, ""),
            };

            return {...state, 
                tiles: state.tiles.map(e => {
                    if (e.Id == widgetId)
                    {
                        return {
                            ...e,
                            requestId: null,
                            properties: properties,
                            item: getWidgetSettingsComponent(e.WidgetTypeId, properties) 
                        };
                    }
                    else
                        return e;
                })
            };
        },

        //grid events handlers
        onGridTempFilter: (state, action) => {           
            //payload {columns}
            const pageState = current(state);
            const tempFilter = action.payload;//.tempFilter;
          
            if (!pageState.currentWidget)
                return state;

            let tile = pageState.tiles.find (e => e.Id == pageState.currentWidget.Id);
            if (!tile)
                return state;

            let properties = {
                ...tile.properties, 
                tempFilter: tempFilter
            };

            return {
                ...state, 
                tiles: state.tiles.map(e => {                   
                    if (e.Id == state.currentWidget.Id)
                        return {
                            ...e, properties: properties, item: getWidgetSettingsComponent(WIDGET_GRID, properties)
                        };
                    else
                        return e;
                })
            }
        },


        onGridColumnReorder: (state, action) => {           
            //payload {columns}
            const pageState = current(state);
            const columns = action.payload.columns;
          
            if (!pageState.currentWidget)
                return state;

            let tile = pageState.tiles.find (e => e.Id == pageState.currentWidget.Id);
            if (!tile)
                return state;

            let properties = {
                ...tile.properties, 
                fields: tile.properties.fields.map(item => {                         
                    //change the position prop of every field
                    //AND then sort the fields accordingly               
                    const column = columns.find(e => e.field == item.name);
                    if (!column) //it is hidden
                        return {...item};
                    else
                        return {...item, orderId: column.orderIndex}
                }).sort((a, b) => (a.orderId > b.orderId) ? 1 : -1)
            };

            return {
                ...state, 
                tiles: state.tiles.map(e => {                   
                    if (e.Id == state.currentWidget.Id)
                        return {
                            ...e, properties: properties, item: getWidgetSettingsComponent(WIDGET_GRID, properties)
                        };
                    else
                        return e;
                })
            }
        },

        onGridColumnResize: (state, action) => {
            //payload {columnId, oldWidth, newWidth, end}
            const pageState = current(state);
            const columnKey = action.payload.columnId.trim();
            const columnId = columnKey.substr(1);
            const oldWidth = action.payload.oldWidth;
            const newWidth = action.payload.newWidth;
            const end = action.payload.end;

            if (!end || !pageState.currentWidget)
                return state;

            let tile = pageState.tiles.find (e => e.Id == pageState.currentWidget.Id);
            if (!tile)
                return state;

            let properties = {
                ...tile.properties, 
                fields: tile.properties.fields.map(item => {                                        
                    if (item.id.toString() != columnId.toString())
                        return item;
            
                    return {...item, width: newWidth};
                })
            };

            return {
                ...state, 
                tiles: state.tiles.map(e => {                   
                    if (e.Id == state.currentWidget.Id)
                        return {
                            ...e, properties: properties, item: getWidgetSettingsComponent(WIDGET_GRID, properties)
                        };
                    else
                        return e;
                })
            }
        },
        
        onGridColumnSettingChange: (state, action) => {
            //payload {columnId, oldWidth, newWidth, end}
            const pageState = current(state);
            const fieldId = action.payload.fieldId;
            const fieldName = action.payload.fieldName;
            const prop = action.payload.prop;
            const value = action.payload.value;

            if (!pageState.currentWidget)
                return state;

            let tile = pageState.tiles.find (e => e.Id == pageState.currentWidget.Id);
            if (!tile)
                return state;

            let properties = {
                ...tile.properties, 
                fields: tile.properties.fields.map(item => {                                        
                    if (item.id != fieldId)
                        return item;
            
                    return {...item, [prop]: value};
                })
            };

            return {
                ...state, 
                tiles: state.tiles.map(e => {                   
                    if (e.Id == state.currentWidget.Id)
                        return {
                            ...e, properties: properties, item: getWidgetSettingsComponent(WIDGET_GRID, properties)
                        };
                    else
                        return e;
                })
            }
        },

        onGridSort: (state, action) => {
            //payload {sort}
            const pageState = current(state);            

            if (!pageState.currentWidget)
                return state;

            let tile = pageState.tiles.find (e => e.Id == pageState.currentWidget.Id);
            if (!tile)
                return state;

            let properties = {
                ...tile.properties, 
                fields: tile.properties.fields.map(field => {   
                    let s = action.payload.sort.find(e => e.field == field.name);

                    if (s)     
                        return {...field, isSorted: true, sortDirection: s.dir};
                    else
                        return {...field, isSorted: false, sortDirection: ""};
            })};


            return {
                ...state, 
                tiles: state.tiles.map(e => {                   
                    if (e.Id == state.currentWidget.Id)
                        return {
                            ...e, properties: properties, item: getWidgetSettingsComponent(WIDGET_GRID, properties)
                        };
                    else
                        return e;
                })
            }
        },

        onGridPageChange: (state, action) => {
            //payload {page}
            const pageState = current(state);            

            if (!pageState.currentWidget)
                return state;
            
            let tile = pageState.tiles.find (e => e.Id == pageState.currentWidget.Id);
            if (!tile)
                return state;

            const page = action.payload.page;
            let skip = page.skip;
            let take = page.take;

            let currentPageIndex = 0;
            if (skip > 0)
                currentPageIndex = Math.ceil(skip / tile.properties.pageSize);

            let properties = {
                ...tile.properties, 
                currentPageIndex: currentPageIndex
            };

            return {
                ...state, 
                tiles: state.tiles.map(e => {                   
                    if (e.Id == state.currentWidget.Id)
                        return {
                            ...e, properties: properties, item: getWidgetSettingsComponent(WIDGET_GRID, properties)
                        };
                    else
                        return e;
                })
            }
        },

        onGridGroupChange: (state, action) => {
            //payload {group}
            const pageState = current(state);  
            let group = action.payload.group;

            if (!pageState.currentWidget)
                return state;

            let tile = pageState.tiles.find (e => e.Id == pageState.currentWidget.Id);
            if (!tile)
                return state;

            let properties = {
                ...tile.properties, 
                fields: tile.properties.fields.map(field => {   
                    let j = group.findIndex(e => e.field == field.name);
                    if ( j >= 0)
                        return ({...field, isGrouped: true, groupOrder: j});
                    else 
                        return ({...field, isGrouped: false, groupOrder: -1});
            })};


            return {
                ...state, 
                tiles: state.tiles.map(e => {                   
                    if (e.Id == state.currentWidget.Id)
                        return {
                            ...e, properties: properties, item: getWidgetSettingsComponent(WIDGET_GRID, properties)
                        };
                    else
                        return e;
                })
            }
        },

        autoGlobalGenerate: (state, action) => {
            const pageState = current(state);

            return state;
        },

        disposePage: (state, action) => {
            //payload: bool  
            if (action.payload)
                return {...initialState};
            else  
                return {...state, pageDisposed: false};
        },
    }
});

export default reportingViewPageSlice.reducer;

export const loadReportPage = (referenceKey, t) => async (dispatch, getState) => {    
    //start with initial values
    dispatch(setInitialState());

    //undispose page
    dispatch(disposePage(false));

    //show loader
    dispatch(setProcessing({processing: true, processingText: t("slices.loadingPage")}));

    try{
        var response = await api({            
                url: '/reportingview/getreportingviewByReferenceKey',
                method:  'post',
                data: {
                   referenceKey
                },
                //headers:{'h':'1'}
        });
        
        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().reportingViewPage.pageDisposed)
           return;
           
        //it's ok to dispatch here
        let data = response.data.Data;
         
        //transform data.SearchSchema to handle variant dates
        let searchSchema = data.SearchSchema;
        if (searchSchema && searchSchema.Rules && searchSchema.Rules.length > 0)
        {
            let whereObject = JSON.parse(searchSchema.WhereObject);
 
            for (let i in searchSchema.Rules)
            {
                let r = searchSchema.Rules[i];

                if (r.ValueKey && r.ValueKey != "0")
                {
                    let whereRule = whereObject.rules.find(w => w.id == r.RuleId);
                   
                    ////let temp = getJsonDateStringFromDateKey(r.ValueKey);
                    let temp = getDateFromDateKey(r.ValueKey);

                    //console.log("Temp is ", temp); 

                    /*detected and fixed in copier and applied here the below replaces the commented code below it*/
                    if (Array.isArray(temp))
                        //r.Value = dateTimeString(new Date(temp[0])) + "," + dateTimeString(new Date(temp[1]));
                        r.Value = dateTimeString(temp[0]) + "," + dateTimeString(temp[1]);
                    else
                        //r.Value = dateTimeString(new Date(temp));          
                        r.Value = dateTimeString(temp);


                    if (whereRule)
                    {
                        if (Array.isArray(temp))
                           //whereRule = [dateTimeString(new Date(temp[0])) , dateTimeString(new Date(temp[1]))];
                           whereRule = [dateTimeString(temp[0]) , dateTimeString(temp[1])];
                        else
                           whereRule.value = dateTimeString(temp);
                    }
                }
            }
 
            searchSchema.WhereObject = JSON.stringify(whereObject)
        }

        
        //transform the response        
        dispatch(reportPageLoaded({data: data, currentUser: getState().currentUser}));

        //console.log("AFTER API STATE IS ", getState().reportingViewPage)
        return Promise.resolve({response: response, pageState: getState().reportingViewPage});
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false        
        return Promise.reject(err);
    }
};

export const updateSchema = (t) => async (dispatch, getState) => {
    let pageState = getState().reportingViewPage;

    dispatch(setProcessing({processing: true, processingText: t("slices.updatingSchema")}));

    //transform the data
    let obj = buildReportObject(pageState);
    try{
        var response = await api({            
            url:"/reportingdesign/updatereport",
            method:  'post',
            data: {...obj,
                saveAsDefault: false,
                groupsExcluded: true
            },
            //headers:{'h':'1'}
        });

        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().reportingViewPage.pageDisposed)
           return;

        let data = response.data.Data;

        //dispatch(schemaUpdated({...data}));

        dispatch(addNotification({text: t("slices.updatedSchema"), type: "success", closable: true, duration: notificationDurationMiddle, position:"topRight"}));

        dispatch(setProcessing({processing: false}));

        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false
        return Promise.reject(err);
    }
};

export const globalGenerateReport = (tiles) => async (dispatch, getState) => {
    let pageState = getState().reportingViewPage;
    let signalRConnectionId = getState().signalR.connectionId;
    //console.log(">>> SignalR Connection Id Before generating report is ", signalRConnectionId);

    let widgets = tiles.map(e => ({
        requestId: e.requestId, 
        widgetId: e.widgetId, 
        dataSourceId: e.dataSourceId,
        whereStatement: e.whereStatement, 
        whereParameters: e.whereParameters, 
        parameters: e.parameters,
        maximumRows: e.dataSourceTypeId == 1 ? null : e.properties.maximumRows
    }));

    //console.log("Widgets", widgets)
    //update tiles
    dispatch(setTiles(tiles));

    //get the tiles that have requestId not null => they passed the validation
    if (tiles.find(e => e.requestId))
    {
        //at least one tile is ok => invoke api
        dispatch(setPageValue({id:"globalGenerate", value: true}));
        dispatch(setPageValue({id:"globalRequestId", value: widgets[0].requestId}));
        
        //transform widgets
        let _widgets = [];

        const requestWidgets = widgets.filter(e => e.requestId);

        const isGlobal = pageState.globalFilter.isVisible;
        const isGlobalParametrized = pageState.datasets[0].IsParametrized;

        let hasGlobalParametersValues = false;
        if (isGlobal && isGlobalParametrized)
            hasGlobalParametersValues = pageState.globalFilter.parameters.findIndex(e => parameterHasValue(e.DataTypeId, e.IsLookup, e.AllowMultiple, e.Value) !== null) >= 0;

        for (let i in requestWidgets)
        {
            const widget = requestWidgets[i];
            const tile = pageState.tiles.find(e => e.Id == widget.widgetId);

            if (isGlobal)
            {
                let hasParametersValues = false;
                if (tile.DatasetIsParametrized)
                    hasParametersValues = tile.parameters.findIndex(e => parameterHasValue(e.DataTypeId, e.IsLookup, e.AllowMultiple, e.Value) !== null) >= 0;
    
                //for the grouping to be ok from a parameters perspective, either the dataset shall not be parametrized in which the whole concept of parameters does not exist
                //or (the tile must not have any parameter value AND (either has combine = true for the global params to apply or can have has combine = false provided that no global parameters exist))
                //if has combine params = true and tile has no params => global params apply no matter whether they exist or not => no problem
                //if has combine params = false, what if there are global params value? in this case the tile must not abide to them because combine is false => tile cannot be grouped
                //this is why if combine is false, we need to make sure the global params have no value
                const _p = !isGlobalParametrized || (!hasParametersValues && (tile.combineParameters == true || !hasGlobalParametersValues));

                //similar to the above for search
                const _b = (!tile.whereParameters || tile.whereParameters.length == 0) && (tile.combineWhere || (!pageState.globalFilter.whereParameters || pageState.globalFilter.whereParameters.length == 0))
                if (_p && _b)
                {             
                    //is candidate to grouping
                    const _widget = _widgets.find(e => e.dataSourceId == widget.dataSourceId && e.isFlexible == true);

                    if (!_widget)
                        _widgets.push({isFlexible: true, requestId: widget.requestId, dataSourceId: widget.dataSourceId, whereStatement: widget.whereStatement, whereParameters: widget.whereParameters, parameters: widget.parameters, widgetsIds:[widget.widgetId], maximumRows: widget.maximumRows ? [widget.maximumRows] : []});
                    else
                    {
                        _widget.widgetsIds.push(widget.widgetId);

                        if (widget.maximumRows)
                            _widget.maximumRows.push(widget.maximumRows);
                    }
                }
                else
                    _widgets.push({isFlexible: false, requestId: widget.requestId, dataSourceId: widget.dataSourceId, whereStatement: widget.whereStatement, whereParameters: widget.whereParameters, parameters: widget.parameters, widgetsIds:[widget.widgetId], maximumRows: widget.maximumRows ? [widget.maximumRows] : []});
            }
            else
            {
                _widgets.push({isFlexible: false, requestId: widget.requestId, dataSourceId: widget.dataSourceId, whereStatement: widget.whereStatement, whereParameters: widget.whereParameters, parameters: widget.parameters, widgetsIds:[widget.widgetId], maximumRows: widget.maximumRows ? [widget.maximumRows] : []});
            }
        }

        try{
            var response = await api({            
                url:"/reportingview/generateReportView",
                method:  'post',
                data: {
                    connectionId: signalRConnectionId,

                    widgets: _widgets.map(e => (
                        {
                            requestId: e.requestId, datasourceId: e.dataSourceId, whereStatement:e.whereStatement, whereParameters: e.whereParameters, parameters: e.parameters, widgetsIds:e.widgetsIds, maximumRows: e.maximumRows.length == 0 ? 0 : Math.max(...e.maximumRows)
                        }))
                },
                //headers:{'h':'1'}
            });
    
            //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
            if(getState().reportingViewPage.pageDisposed)
               return;
    
            return Promise.resolve(response);
        }
        catch(err)
        {
            //i am here => could be any error included 200 and Succeeded=false
            return Promise.reject(err);
        }
    }    
};

export const widgetGenerateReport = (widgetId, newRequestId, t) => async (dispatch, getState) => {
    let pageState = getState().reportingViewPage;    
    
    let signalRConnectionId = getState().signalR.connectionId;
    //console.log(">>> SignalR Connection Id Before generating report is ", signalRConnectionId);

    let tile = pageState.tiles.find(e => e.Id == widgetId);
    let dataSourceId = tile.DataSourceId;

    //widget must not be in a loading state
    if (tile.properties.loading)
        return Promise.reject(t("slices.invalidState"));        

    let globalWhereStatement = "";
    let globalWhereParameters = [];
    let globalParameters = [];

    if (pageState.globalFilter.isVisible)
    {
        globalWhereStatement = pageState.globalFilter.whereStatement;
        globalWhereParameters = (pageState.globalFilter.whereParameters == "" || !pageState.globalFilter.whereParameters || !Array.isArray(pageState.globalFilter.whereParameters)) ? [] : [...pageState.globalFilter.whereParameters];
        globalParameters = (pageState.globalFilter.parameters == "" || !pageState.globalFilter.parameters || !Array.isArray(pageState.globalFilter.parameters)) ? [] : [...pageState.globalFilter.parameters];
    }

    let whereStatement = tile.whereStatement;
    // let whereParameters = [...tile.whereParameters];
    // let parameters = [...tile.parameters];
    let whereParameters = (tile.whereParameters == "" || !tile.whereParameters || !Array.isArray(tile.whereParameters)) ? [] : [...tile.whereParameters];
    let parameters = (tile.parameters == "" || !tile.parameters || !Array.isArray(tile.parameters)) ? [] : [...tile.parameters];
    let combinedParameters = [];

    //handle parameters if SP
    if (tile.DatasetIsParametrized)
    {
        //tile has parameters; globalParameters will be/not be empty based on whether there is global filter or not which is already handled
        combinedParameters = tile.combineParameters ? combineParameters(globalParameters, parameters) : parameters;

        let res = validateParameters(combinedParameters);
        if (res.status == false)            
            {
                dispatch(generateWidget({
                    requestId: null, widgetId: tile.Id, widgetTypeId: tile.WidgetTypeId, dataSourceId: dataSourceId, 
                    loaded: false, loading: false, errorText: t("slices.missingParams"), canceled: false
                }));

                return;
            } 
        else                
            combinedParameters = res.parameters.map(e => ({Key: e.Name, Value: e.TransformedValue}));                
    }

    
    //handle search
    if (whereStatement && whereStatement.trim() != "")
    {
        if (tile.combineWhere && globalWhereStatement && globalWhereStatement.trim() != "")
        {
            whereStatement = "(" + globalWhereStatement + ")" + (tile.combineWhereOperator == 1 ? " AND " : " OR ") + "(" + whereStatement + ")";
            whereParameters = [...globalWhereParameters, ...whereParameters];
        }
        //else do nothing because where statement and parameters are already filled
    }
    else if (tile.combineWhere && globalWhereStatement && globalWhereStatement.trim() != "")
    {
        whereStatement = globalWhereStatement;
        whereParameters = [...globalWhereParameters];
    }

    let obj = {
        requestId: newRequestId, widgetId: tile.Id, widgetTypeId: tile.WidgetTypeId, dataSourceId: dataSourceId, 
        loaded: false, loading: true, errorText: "", canceled: false,
        whereStatement: whereStatement, whereParameters: whereParameters, parameters: combinedParameters,
        maximumRows: tile.properties.maximumRows ? tile.properties.maximumRows : 0
    };

    //move the widget into the loading state
    dispatch(generateWidget(obj));
  
    try{
        var response = await api({            
            url:"/reportingview/generateReportView",
            method:  'post',
            data: {
                connectionId: signalRConnectionId,

                widgets: [{requestId: obj.requestId, dataSourceId: obj.dataSourceId, whereStatement: obj.whereStatement, whereParameters: obj.whereParameters,
                parameters: obj.parameters, widgetsIds:[obj.widgetId], maximumRows: obj.maximumRows}]
            },
            //headers:{'h':'1'}
        });

        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().reportingViewPage.pageDisposed)
            return;

        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => error => i must show notification here because this action was invoked from utils and it's very hard if possible to do this there
        tile = getState().reportingViewPage.tiles.find(e => e.Id == widgetId);

        let requestIdChanged = tile.requestId != newRequestId;

        //cancel anyway
        dispatch(cancelWidget(widgetId));

        //do not show notification if failed because timeout and global request id is notnewRequestId
        if(err && err.succeeded == false && err.timedOut && requestIdChanged)
            return Promise.reject(err);

        //show notification
        const handleResponseReceived = (state, dispatch, navigate, payload) =>{
            let errorMessages = payload?.data?.ErrorMessages;

            //notify with error
            if (!errorMessages || errorMessages.length == 0)
                dispatch(addNotification({text: t("slices.unknownError"), type: "error", closable: true, duration:notificationDurationMiddle, position:"topRight"}));
            else
            {
                errorMessages = getErrorMessages(errorMessages, 1);
                dispatch(addNotification({text: errorMessages[0], type: "error", closable: true, duration:notificationDurationMiddle, position:"topRight"}));
            }
        };

        const handleUnhandled = (state, dispatch, navigate, payload) =>{
            dispatch(addNotification({text:t("slices.errorOccured") , type:"error", closable: true, duration: notificationDurationMiddle, position: "topRight"}));
        };

        apiErrorsHandler({}, dispatch, null, err, null, {responseReceived: handleResponseReceived, unhandled: handleUnhandled});

        return Promise.reject(err);
    }    
};

export const gridExportToExcel = (widgetId) => async (dispatch, getState) => {
    let pageState = getState().reportingViewPage;    

    dispatch(gridExportToExcelOn(widgetId));

    setTimeout(() => { dispatch(gridExportToExcelOff(widgetId))}, 1000);
};

export const saveSearchSchema = (t) => async (dispatch, getState) => {    
    dispatch(setProcessing({processing: true, processingText: t("slices.savingSearch")}));

    try{
        let body = {...getState().reportingViewPage.searchSchemaTemp};

        if (body.rules && body.rules.length > 0)        
            body.rules = body.rules.map(e => ({...e, value: e.value ? e.value.toString() : null}));
        else
            body.rules = [];

        //console.log("Saving search schema: ", body);

        var response = await api({            
            url: 'reportingview/createreportsearchschema',
            method:  'post',
            data: {
                ...body
            },
            //headers:{'h':'1'}
        });
        
        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().reportingViewPage.pageDisposed)
           return;
           
        //it's ok to dispatch here
        let data = response.data.Data;
                       
        dispatch(addNotification({text: t("slices.savingSearch"), type: "success", closable: true, duration: notificationDurationMiddle, position:"topRight"}));

        //transform the response        
        dispatch(searchSchemaSaved({data: data}));

        //save the filter form itself-added in oct 2023
        dispatch(filterFormSave());

        dispatch(setProcessing({processing: false, processingText: ""}));

        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false        
        return Promise.reject(err);
    }
};

export const { 
    setInitialState, setProcessing, setPageValue, pageLoaded, setSettingsFormValue, setSettingsFormOuterValue, setSettingsFormSettingValue, setFilterFormValue, disposePage, 
    reportPageLoaded, showSettingsForm, closeSettingsForm, settingsFormPrevious, settingsFormNext, settingsFormSave, resetTileWidget, selectWidget, unSelectWidget, 
    clearFilterForm, closeFilterForm, 
    onGridColumnResize, onGridColumnSettingChange, onGridSort, onGridPageChange, onGridGroupChange, onGridColumnReorder, onGridTempFilter,
    showFilterForm, setFilterFormParameter, filterFormSave, filterFormPrevious, filterFormNext, setFilterFormOuterValue,
    zoomIn, zoomOut, setTiles, reportViewGeneratedSucceeded, reportViewGeneratedFailed, globalCancelReport,
    generateWidget, cancelWidget, gridExportToExcelOn, gridExportToExcelOff, searchSchemaSaved, showFilterFormValueKeys, closeFilterFormValueKeys, setFilterFormValueKeysValue,
    loadSchemaSearch, autoGlobalGenerate, redirectToSchedulePage
 } = reportingViewPageSlice.actions;
